/**
 * Light/dark theme preference persistence. Stores the choice under the `theme`
 * localStorage key and falls back to the OS `prefers-color-scheme` on first
 * visit. Wired into `UIProvider` in `main.tsx` (`defaultTheme` seeds initial
 * render, `onThemeChange` writes back on toggle).
 */
type ThemePreference = 'light' | 'dark';

const STORAGE_KEY = 'theme';

/** Initial theme: stored choice → OS preference → light. */
export function resolveInitialTheme(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage unavailable (private mode) — fall through to OS preference.
  }
  if (
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches
  ) {
    return 'dark';
  }
  return 'light';
}

/** Persist the chosen theme so it survives reloads. */
export function persistTheme(theme: ThemePreference): void {
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Best-effort — ignore storage failures (private mode / quota).
  }
}
